import { useState, useMemo } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { WORKSHOP_PAGES, type WorkshopPage } from "./workshop-routes";
import { WorkshopSearchProvider } from "./workshop-search-context";

const CATEGORIES = [
  { key: "pages", label: "Pages" },
  { key: "journeys", label: "Journeys" },
  { key: "shapes", label: "Shapes" },
  { key: "panels", label: "Panels" },
  { key: "canvas", label: "Canvas" },
  { key: "cursors", label: "Cursors" },
  { key: "layout", label: "Layout" },
] as const;

/** Routes that show the top navbar instead of the floating nav */
const NAVBAR_ROUTES = new Set(["", "sync-status"]);

// ─── Floating nav (original — for component/page preview routes) ──

function NavSection({ label, pages }: { label: string; pages: WorkshopPage[] }) {
  if (pages.length === 0) return null;

  return (
    <div className="mb-3">
      <h3 className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-wider text-white/40">
        {label}
      </h3>
      <ul className="space-y-0.5">
        {pages.map((page) => (
          <li key={page.path}>
            <NavLink
              to={`/workshop/${page.path}`}
              className={({ isActive }) =>
                cn(
                  "block rounded-md px-3 py-1.5 text-sm transition-colors",
                  isActive
                    ? "bg-accent/15 text-accent"
                    : "text-white/60 hover:bg-white/5 hover:text-white/80",
                )
              }
              data-testid={`workshop-nav-${page.path}`}
            >
              {page.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FloatingNav() {
  const [menuOpen, setMenuOpen] = useState(false);

  const grouped = useMemo(
    () =>
      CATEGORIES.map(({ key, label }) => ({
        label,
        pages: WORKSHOP_PAGES.filter((p) => p.category === key),
      })),
    [],
  );

  const hasPages = WORKSHOP_PAGES.length > 0;

  return (
    <div className="pointer-events-none fixed inset-0 z-50">
      <div
        className="pointer-events-auto absolute bottom-4 left-4"
        onMouseEnter={() => setMenuOpen(true)}
        onMouseLeave={() => setMenuOpen(false)}
        data-testid="workshop-nav-floating"
      >
        {menuOpen && (
          <div
            className={cn(
              "mb-2 w-56 rounded-xl border border-white/10 bg-[#1a1a1c]/95 p-3",
              "shadow-lg backdrop-blur-md",
            )}
            data-testid="workshop-nav-menu"
          >
            <div className="mb-2 border-b border-white/[0.06] pb-2">
              <NavLink
                to="/workshop"
                className="text-sm font-semibold text-accent hover:text-accent/80"
                data-testid="workshop-nav-home"
              >
                Workshop
              </NavLink>
              <p className="mt-0.5 text-[11px] text-white/30">
                UI Development Sandbox
              </p>
            </div>

            <nav className="max-h-64 overflow-y-auto">
              {hasPages ? (
                grouped.map(({ label, pages }) => (
                  <NavSection key={label} label={label} pages={pages} />
                ))
              ) : (
                <p className="px-3 text-xs text-white/25">
                  No pages yet. Register pages in workshop-routes.tsx.
                </p>
              )}
            </nav>

            <div className="mt-2 flex items-center justify-between border-t border-white/[0.06] pt-2">
              <NavLink
                to="/"
                className="text-xs text-white/30 hover:text-white/50"
                data-testid="workshop-nav-back"
              >
                &larr; Back to App
              </NavLink>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          className={cn(
            "flex h-10 w-10 items-center justify-center rounded-full",
            "border border-white/10 bg-[#1a1a1c]/90 shadow-lg backdrop-blur-md",
            "text-white/60 transition-colors hover:text-white",
          )}
          aria-label="Toggle workshop navigation"
          data-testid="workshop-nav-trigger"
        >
          <Icons.Layers className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ─── Top navbar (for workshop index + sync-status only) ───

function Navbar({ search, onSearchChange }: { search: string; onSearchChange: (v: string) => void }) {
  return (
    <header
      className="shrink-0 border-b border-white/[0.06] bg-[#0A0A0B]/95 backdrop-blur-sm"
      data-testid="workshop-navbar"
    >
      <div className="flex h-11 items-center gap-3 px-4">
        <NavLink
          to="/workshop"
          className="flex items-center gap-2 rounded-md px-2 py-1 text-sm font-semibold text-accent transition-colors hover:text-accent/80"
          data-testid="workshop-nav-home"
        >
          <Icons.Layers className="h-4 w-4" />
          Workshop
        </NavLink>

        <NavLink
          to="/workshop/sync-status"
          className={({ isActive }) =>
            cn(
              "flex items-center gap-1.5 rounded-md px-2 py-1 text-xs transition-colors",
              isActive
                ? "bg-accent/10 text-accent"
                : "text-white/40 hover:bg-white/5 hover:text-white/60",
            )
          }
          data-testid="workshop-nav-sync"
        >
          <Icons.GitBranch className="h-3.5 w-3.5" />
          Sync Status
        </NavLink>

        <div className="flex-1" />

        {/* Search — filters content on current page */}
        <div className="relative" data-testid="workshop-search">
          <Icons.Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/30" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Filter..."
            className={cn(
              "h-8 w-52 rounded-md border border-white/[0.08] bg-white/[0.03] pl-8 pr-8",
              "text-xs text-white/80 placeholder:text-white/25",
              "outline-none transition-colors focus:border-accent/30 focus:bg-white/[0.05]",
            )}
            data-testid="workshop-search-input"
          />
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60"
              data-testid="workshop-search-clear"
            >
              <Icons.X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <NavLink
          to="/"
          className={cn(
            "flex items-center gap-1 rounded-md px-2 py-1",
            "text-xs text-white/30 transition-colors hover:bg-white/5 hover:text-white/50",
          )}
          data-testid="workshop-nav-back"
        >
          Back to App
          <Icons.ExternalLink className="h-3 w-3" />
        </NavLink>
      </div>
    </header>
  );
}

// ─── Layout ───────────────────────────────────────────────

export function WorkshopLayout() {
  const [search, setSearch] = useState("");
  const location = useLocation();

  const routeKey = location.pathname
    .replace("/workshop/", "")
    .replace("/workshop", "");

  const showNavbar = NAVBAR_ROUTES.has(routeKey);

  if (showNavbar) {
    return (
      <div
        className="flex h-screen flex-col bg-[#0A0A0B] text-white"
        data-testid="workshop-layout"
      >
        <Navbar search={search} onSearchChange={setSearch} />
        <main
          className="flex-1 overflow-y-auto bg-canvas"
          data-testid="workshop-content"
        >
          <WorkshopSearchProvider value={search}>
            <Outlet />
          </WorkshopSearchProvider>
        </main>
      </div>
    );
  }

  // Original layout — full-screen content + floating nav
  return (
    <div
      className="relative h-screen bg-[#0A0A0B] text-white"
      data-testid="workshop-layout"
    >
      <main
        className="h-full w-full overflow-y-auto bg-canvas"
        data-testid="workshop-content"
      >
        <Outlet />
      </main>
      <FloatingNav />
    </div>
  );
}
